import React, { Component } from "react";
import DatePicker, { CalendarContainer } from "react-datepicker";
import moment from "moment-timezone";
import empty from "is-empty";
import momentDurationFormatSetup from "moment-duration-format";

import { COUNTRY_OPTIONS } from "src/redux/MarketData/constants";
import * as Validators from "src/utils/validators";
import RadioButtons from "src/components/common/RadioButton";

import "react-datepicker/dist/react-datepicker.css";
import styles from "./styles.module.scss";
import { getClampedMaturityDate } from "src/utils";

let prevPlacement = "bottom-end";
momentDurationFormatSetup(moment);

const getPopperPlacement = () => {
    let popperPlacement = "bottom-end";
    if (
        document.hasFocus() &&
        document.activeElement !== document.body &&
        document.activeElement !== document.documentElement
    ) {
        if (document.activeElement.parentNode.className.includes("ag-row")) {
            const { top } = document.activeElement.getBoundingClientRect();
            const vh = Math.max(
                document.documentElement.clientHeight || 0,
                window.innerHeight || 0
            );

            if (vh - top < top) {
                popperPlacement = "top-end";
            }
        } else {
            // if an element inside the datepicker is active
            return prevPlacement;
        }
    }
    prevPlacement = popperPlacement;
    return popperPlacement;
};

const INDEFINITE_DATE = "9999/12/31";
const MATURITY_TYPES = {
    INDEFINITE: "INDEFINITE",
    CUSTOM: "CUSTOM",
};

export default class DatePickerEditor extends Component {
    constructor(props) {
        super(props);

        this.state = {
            value: props.value,
        };
    }

    isPopup = () => {
        return true;
    };

    getValue = () => {
        return this.state.value;
    };

    handleDatePicked = (tradeableDates) => (input, e) => {
        // NOTE: stop auto completing incomplete date string
        if (e.target.value && e.target.value.replace(/[/-]/g, "").length < 8)
            return;

        const { data, colDef } = this.props;
        const isEditingMaturity =
            colDef.field === "Maturity" || colDef.field === "maturity_date";
        const isDatePickedByClick = !e.target.value;
        const isEditingSettlementDate = colDef.field === "settlementDate";

        let value = "";
        if (input) {
            value = Validators.convertDateToStr(input);
        } else if (isEditingMaturity) {
            value = INDEFINITE_DATE;
        }

        const isValidValueDate =
            e.target.value &&
            tradeableDates.filter(
                (d) => d === e.target.value.replace(/[/-]/g, "")
            ).length === 1;
        const isValidMaturity =
            !empty(value) &&
            moment(data.ValueDate || data.value_date).isSameOrBefore(
                moment(value)
            );
        const isValidSettlementDate =
            !empty(value) && moment(value).isSameOrBefore(moment(data.dueDate));

        if (
            isDatePickedByClick ||
            isValidValueDate ||
            (isEditingMaturity && isValidMaturity) ||
            (isEditingSettlementDate && isValidSettlementDate)
        ) {
            this.setState({ value }, () => {
                this.props.api.stopEditing();
            });
        }
    };

    handleTypeChange = (minDate) => (type) => {
        if (type === MATURITY_TYPES.CUSTOM) {
            this.setState({ value: Validators.convertDateToStr(minDate) });
        } else {
            this.setState({ value: INDEFINITE_DATE }, () => {
                this.props.api.stopEditing();
            });
        }
    };

    render() {
        const { value } = this.state;
        const { cellStartedEdit, column, data, colDef, context } = this.props;
        const allowPastDates = this.props.allowPastDates || false;

        const isEditingMaturity =
            colDef.field === "Maturity" || colDef.field === "maturity_date";
        const isEditingSettlementDate = colDef.field === "settlementDate";
        const enabled90DayMaxMaturity =
            context?.featureFlags?.["enable-90-day-max-maturity-date"];
        const marketHours =
            COUNTRY_OPTIONS[
                data.Country || data.security_country || data.country
            ];

        if (!marketHours) return <div />;
        if (isEditingSettlementDate && !data.dueDate) return <div />;

        const tradeableDates =
            data.TradeableDates ||
            (data.security_tradable_dates
                ? data.security_tradable_dates
                      .split(",")
                      .map((s) => s.replace(/-/g, ""))
                : []);

        let minDate =
            empty(tradeableDates) || allowPastDates
                ? null
                : moment(tradeableDates[0]).toDate();
        if (isEditingMaturity) {
            minDate = moment(data.ValueDate || data.value_date).toDate();
        } else if (isEditingSettlementDate) {
            minDate = null;
        }

        let maxDate = null;
        if (isEditingSettlementDate) {
            maxDate = moment(data.dueDate).toDate();
        } else if (
            isEditingMaturity &&
            enabled90DayMaxMaturity &&
            context.role === "borrowing"
        ) {
            maxDate = moment(
                getClampedMaturityDate(
                    data.Country,
                    data.Purpose,
                    data.Maturity,
                    data.ValueDate,
                    data.TradeableDates,
                    data.maxMaturityDates,
                    enabled90DayMaxMaturity
                )
            ).toDate();
        }

        const isDateSelectable = (date) => {
            if (isEditingMaturity || isEditingSettlementDate) return true;
            if (empty(tradeableDates)) return true;
            return Validators.isDateValid(
                Validators.convertDateToStr(date),
                tradeableDates,
                allowPastDates
            );
        };

        let selected = null;
        let maturityType = MATURITY_TYPES.INDEFINITE;
        if (value) {
            const momentDate = moment(value);
            if (momentDate.isValid()) {
                if (momentDate.format("YYYY/MM/DD") !== INDEFINITE_DATE)
                    maturityType = MATURITY_TYPES.CUSTOM;
                selected = momentDate.tz(marketHours.zone).toDate();
            }
        } else if (!isEditingMaturity) {
            selected = moment(tradeableDates[1]).toDate();
        }

        return (
            <div style={{ width: `${column.actualWidth}px` }}>
                <DatePicker
                    calendarContainer={(headerProps) => (
                        <CustomCalendar
                            {...headerProps}
                            isEditingMaturity={isEditingMaturity}
                            maturityType={maturityType}
                            onChange={this.handleTypeChange(minDate)}
                        />
                    )}
                    selected={selected}
                    onChange={this.handleDatePicked(tradeableDates)}
                    dateFormat={"yyyy/MM/dd"}
                    minDate={minDate}
                    maxDate={maxDate}
                    open={cellStartedEdit}
                    className={styles.datePicker}
                    filterDate={isDateSelectable}
                    onFocus={(e) => e.target.select()} // to make test easier
                    popperClassName={
                        isEditingMaturity ? "maturity-datepicker" : ""
                    }
                    popperPlacement={getPopperPlacement()}
                    popperModifiers={{
                        flip: {
                            enabled: false,
                        },
                    }}
                    allowSameDay
                    openToDate={
                        maturityType === MATURITY_TYPES.INDEFINITE
                            ? minDate
                            : selected
                    }
                />
            </div>
        );
    }
}

const CustomCalendar = ({
    className,
    children,
    isEditingMaturity,
    maturityType,
    onChange,
}) => {
    const MATURITY_OPTIONS = [
        { label: INDEFINITE_DATE, value: MATURITY_TYPES.INDEFINITE },
        { label: "Custom", value: MATURITY_TYPES.CUSTOM },
    ];

    return (
        <CalendarContainer className={className}>
            <div className={"react-datepicker__triangle"} />
            {isEditingMaturity ? (
                <div className={styles.maturityHeader}>
                    <RadioButtons
                        options={MATURITY_OPTIONS}
                        dataTestId="select-maturity-type"
                        value={maturityType}
                        setter={onChange}
                        theme={"white"}
                    />
                </div>
            ) : null}
            <div style={{ position: "relative" }}>{children}</div>
        </CalendarContainer>
    );
};
